import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { programContent } from '../../data/program';
import ProgramNav from '../../components/program/ProgramNav';
import AnimatedSection from '../../components/common/AnimatedSection';
import { 
  ArrowRight, Building2, Scale, Landmark, Users, 
  ChevronDown, Globe, BookOpen, Briefcase
} from 'lucide-react';

const ProgramPage = () => {
  const { language } = useLanguage();
  const content = programContent[language]?.landing || programContent.en.landing;

  const transformationIcons = {
    institutional: Landmark,
    economic: Briefcase,
    social: Users
  };

  const transformationColors = {
    institutional: 'bg-[#5B2D8E]/10 text-[#5B2D8E] border-[#5B2D8E]/20',
    economic: 'bg-[#D4880D]/10 text-[#D4880D] border-[#D4880D]/20',
    social: 'bg-[#0D7377]/10 text-[#0D7377] border-[#0D7377]/20'
  };

  return (
    <div className="pt-24">
      <ProgramNav />
      
      {/* Hero Section */}
      <section className="section bg-gradient-to-b from-[#FFFEF9] to-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#5B2D8E]/5 rounded-full mb-6">
                <Globe className="w-4 h-4 text-[#5B2D8E]" />
                <span className="text-sm font-medium text-[#5B2D8E]">Continental Political Alliance</span>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.1}>
              <h1 className="heading-display mb-6">
                {content.hero.title}
              </h1>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <p className="text-xl lg:text-2xl text-[#6B6B6B] mb-10" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.hero.subtitle}
              </p>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/program/chapters" className="btn-primary">
                  {content.hero.primaryCta}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/program/branded-parties" className="btn-secondary">
                  {content.hero.secondaryCta}
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Three Transformations */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-12">
            <span className="caption mb-4 block">Core Framework</span>
            <h2 className="heading-1 mb-4">{content.transformations.title}</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {content.transformations.items.map((item, index) => {
              const Icon = transformationIcons[item.id];
              const colorClass = transformationColors[item.id];
              return (
                <AnimatedSection key={item.id} delay={index * 0.15}>
                  <Link 
                    to={item.link}
                    className="block h-full bg-white rounded-xl border border-[#E8E0D4] p-8 hover:shadow-lg hover:border-[#5B2D8E]/30 transition-all group"
                  >
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${colorClass} border`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="heading-3 mb-4 group-hover:text-[#5B2D8E] transition-colors">
                      {item.title}
                    </h3>
                    <p className="body-medium text-[#6B6B6B] mb-6">
                      {item.summary}
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-[#5B2D8E] group-hover:gap-3 transition-all">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Continental Model */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              <AnimatedSection className="lg:col-span-2">
                <div className="aspect-square bg-[#5B2D8E]/5 rounded-2xl flex items-center justify-center">
                  <Globe className="w-32 h-32 text-[#5B2D8E]/30" />
                </div>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2} className="lg:col-span-3">
                <span className="caption mb-4 block">{content.continentalModel.title}</span>
                <div className="divider mb-6" />
                <p className="text-lg text-[#2D2D2D] leading-relaxed" style={{ fontFamily: 'var(--font-serif)' }}>
                  {content.continentalModel.description}
                </p>
                <Link to="/program/branded-parties" className="btn-ghost mt-6 inline-flex">
                  Explore the model <ArrowRight className="w-4 h-4" />
                </Link>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Program Architecture */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-12">
            <span className="caption mb-4 block">Structure</span>
            <h2 className="heading-1 mb-4">{content.architecture.title}</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="max-w-3xl mx-auto space-y-4">
            {content.architecture.items.map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <details className="group bg-white rounded-xl border border-[#E8E0D4] overflow-hidden">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <div className="flex items-center gap-4">
                      <span className="w-10 h-10 bg-[#5B2D8E]/10 rounded-lg flex items-center justify-center text-[#5B2D8E] font-bold">
                        {index + 1}
                      </span>
                      <h3 className="font-semibold text-[#2D2D2D]">{item.title}</h3>
                    </div>
                    <ChevronDown className="w-5 h-5 text-[#6B6B6B] group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-6 pb-6 pt-2">
                    <p className="text-[#6B6B6B] pl-14">{item.description}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Navigation Blocks */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* House of the Alliance */}
            <AnimatedSection>
              <Link 
                to="/program/house"
                className="block h-full bg-[#5B2D8E] rounded-2xl p-8 lg:p-10 text-white hover:bg-[#4A2575] transition-all group"
              >
                <Building2 className="w-12 h-12 mb-6 text-[#F5A623]" />
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                  {content.navigation.house.title}
                </h3>
                <p className="text-[#F5A623] text-sm font-medium mb-4">
                  {content.navigation.house.subtitle}
                </p>
                <p className="text-white/80 mb-6">
                  {content.navigation.house.description}
                </p>
                <span className="inline-flex items-center gap-2 text-[#F5A623] font-medium group-hover:gap-3 transition-all">
                  Explore <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </AnimatedSection>

            {/* Shadow Cabinet */}
            <AnimatedSection delay={0.15}>
              <Link 
                to="/program/shadow-cabinet"
                className="block h-full bg-white rounded-2xl border border-[#E8E0D4] p-8 lg:p-10 hover:shadow-lg hover:border-[#5B2D8E]/30 transition-all group"
              >
                <Scale className="w-12 h-12 mb-6 text-[#5B2D8E]" />
                <h3 className="text-2xl font-bold text-[#2D2D2D] mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                  {content.navigation.cabinet.title}
                </h3>
                <p className="text-[#5B2D8E] text-sm font-medium mb-4">
                  {content.navigation.cabinet.subtitle}
                </p>
                <p className="text-[#6B6B6B] mb-6">
                  {content.navigation.cabinet.description}
                </p>
                <span className="inline-flex items-center gap-2 text-[#5B2D8E] font-medium group-hover:gap-3 transition-all">
                  Explore <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <BookOpen className="w-12 h-12 mx-auto mb-6 text-[#5B2D8E]" />
              <h2 className="heading-2 mb-4">Ready to dive deeper?</h2>
              <p className="body-large mb-8">
                Explore the complete program chapters detailing ADA's vision for Africa's transformation.
              </p>
              <Link to="/program/chapters" className="btn-primary">
                Read the Full Program
                <ArrowRight className="w-4 h-4" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramPage;
